import React from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Row, useTable } from "react-table";
import { LoadingIcon } from "../../assets/svg/loadingIcon";
import { IAgTableV2InfiniteScrollProps } from "./AgTableV2InfiniteScroll.props";

const AgTableInfiniteScroll: React.FC<IAgTableV2InfiniteScrollProps> = ({
  columns,
  data,
  maxLength,
  page,
  entriesPerPage = 8,
  onHandleNextPage,
  pageCount,
  isClickOnRow = false,
  onClickOnRow,
  textNoDataFound = "No Records Found",
  isLoading = false,
  isClickOnCell = false,
  onClickOnCell,
  disabledClickOnColumnIds = [],
  noHeader = false,
  loader,
  fixedHeaderScrollHeight,
  scrollableTarget,
  onLoadMoreData = () => { },
  onRowClick,
  renderRowExpandedComponent,
  ...props
}: IAgTableV2InfiniteScrollProps) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data,
  });

  const renderRow = React.useCallback((row: Row<any>, idx: number) => {
    prepareRow(row);
    return (
      <tr
        {...row.getRowProps()}
        className={`mt-15 border-t-[10px] border-solid border-[#e4e4e4] ${isClickOnRow ? "cursor-pointer hover:opacity-80" : ""
          }`}
        onClick={(e) => {
          isClickOnRow && onClickOnRow && onClickOnRow(row.original, e);
        }}
      >
        {row.cells.map((cell: any, j) => {
          return (
            <td
              onClick={(e) => {
                if (disabledClickOnColumnIds.includes(j)) {
                  return;
                }
                isClickOnCell && onClickOnCell && onClickOnCell(row.original, e);
              }}
              rowSpan={cell.rowSpan}
              {...cell.getCellProps()}
              className={`${isClickOnCell ? "cursor-pointer hover:opacity-80" : ""
                } py-[20px] pr-[10px] first:pl-[20px] text-[14px] font-normal text-[#637381] text-start ${cell.column.Header === "Action"
                  ? "sticky right-0 z-10 bg-white pl-[5px]"
                  : ""
                }`}
            >
              {cell.render("Cell")}
            </td>
          );
        })}
      </tr>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepareRow, onRowClick, renderRowExpandedComponent]);

  return (
    <div className="bg-white">
      <InfiniteScroll
        hasMore={data ? data.length < (maxLength as number) : false}
        dataLength={data.length}
        loader={<div className="flex flex-row px-[20px] py-[5px] justify-center border-t-2">
          <LoadingIcon
            width={60}
            height={60}
          />
        </div>}
        next={onLoadMoreData}
        {...(scrollableTarget ? { scrollableTarget } : {})}
        scrollThreshold={0.9}
      >
        <table
          {...getTableProps()}
          className="w-full table-auto"
        >
          {noHeader ? (
            <>
              <colgroup>
                {columns.map((column, index) => (
                  <col
                    key={index}
                    style={{ width: column.width ?? "auto" }}
                  />
                ))}
              </colgroup>
              <thead style={{ display: "none" }}></thead>
            </>
          ) : (
            <thead className={`sticky top-[170px] bg-white`}>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps()}
                      className={`py-[20px] pr-[10px] first:pl-[20px] last:pr-[10px] text-[0.9375rem] font-bold text-start text-[#58585B] ${column.Header === "Action" ? "sticky right-0 z-10 bg-white pl-[5px]" : ""
                        }`}
                      style={{
                        width: `${column.width ?? ""}`,
                      }}
                    >
                      {column.render("Header")}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
          )}
          <tbody {...getTableBodyProps()}>
            {rows.map((row, i) => renderRow(row, i))}
          </tbody>
        </table>

        {
          data && data.length ? (
            <></>
          ) : isLoading ? (
            <div className="flex flex-row px-[20px] py-[5px] justify-center border-t-[1px]">
              <LoadingIcon
                width={60}
                height={60}
              />
            </div>
          ) : (
            <div className="flex flex-row px-[20px] py-[5px] justify-center border-t-[1px] border-solid border-[#EAEAEA]">
              <p className="text-[14px] font-normal text-[#637381]">
                {textNoDataFound ?? "No Records Found"}
              </p>
            </div>
          )
        }
      </InfiniteScroll>
    </div>
  );
};

export default AgTableInfiniteScroll;
